import { useState } from "react";
import dayjs from "dayjs";
import {
  Box,
  Button,
  Popover,
  Tabs,
  Tab,
  Divider,
  List,
  ListItemButton,
  ListItemText
} from "@mui/material";
import RadioButtonCheckedIcon from '@mui/icons-material/RadioButtonChecked';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import { DateCalendar } from "@mui/x-date-pickers/DateCalendar";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { WeekDay } from "./WeekDay";
const minDate = dayjs("2025-01-01");
const maxDate = dayjs();
const PRESETS = [
  {
    label: "Last 7 Days",
    getValue: () => [dayjs().subtract(6, "day"), dayjs()]
  },
  {
    label: "Last 30 Days",
    getValue: () => [dayjs().subtract(29, "day"), dayjs()]
  },
  {
    label: "Last 90 Days",
    getValue: () => [dayjs().subtract(89, "day"), dayjs()]
  },
  {
    label: "Last Week",
    getValue: () => [
      dayjs().startOf("week").subtract(1, "week"),
      dayjs().startOf("week").subtract(1, "day")
    ]
  },
  {
    label: "Last Month",
    getValue: () => [
      dayjs().subtract(1, "month").startOf("month"),
      dayjs().subtract(1, "month").endOf("month")
    ]
  },
  {
    label: "Year to Date",
    getValue: () => [dayjs().startOf("year"), dayjs()]
  },
  {
    label: "Lifetime",
    getValue: () => [dayjs("2025-09-23"), dayjs()]
  }
];
const AllTabs=['Presets','Day','Week','Month','Year'];
const DateRangeFilter = ({ onChange }) => {
  
  const [tab, setTab] = useState(0);
  const [preset, setPreset] = useState(6);
  const [anchorEl, setAnchorEl] = useState(null);

  const [startDate, setStartDate] = useState(dayjs("2025-09-23"));
  const [endDate, setEndDate] = useState(dayjs());

  const open = Boolean(anchorEl);

  const handleOpen = (e) => {
    setAnchorEl(e.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const applyRange = (start, end) => {
    setStartDate(start);
    setEndDate(end);

    if (onChange) {
      onChange({
        start_date: start.format("YYYY-MM-DD"),
        end_date: end.format("YYYY-MM-DD")
      });
    }

    handleClose();
  };

  const applyPreset = (preset, i) => {
    const [start, end] = preset.getValue();
    setStartDate(null);
    setEndDate(null);
    setPreset(i);
    applyRange(start, end);
  };

  const handleApply = () => {
    applyRange(startDate, endDate);
  };

  const handleDayChange = (date) => {
    setStartDate(date);
    setEndDate(date);
    setPreset(-1);
  };

  const handleWeekChange = (date) => {
    setStartDate(date.startOf("week"));
    setEndDate(date.endOf("week"));
    setPreset(-1);
  };

  const handleMonthChange = (date) => {
    setStartDate(date.startOf("month"));
    setEndDate(date.endOf("month"));
    setPreset(-1);
  };

  return (
    <>
      <Button
        variant="contained"
        onClick={handleOpen}
        sx={{
          borderRadius: "20px",
          textTransform: "none",
          background: "#1f6feb"
        }}
      >
        {startDate.format("MMM D, YYYY")} – {endDate.format("MMM D, YYYY")}
      </Button>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right"
        }}
      >
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <Box width={540}>

            {/* Tabs */}
            <Tabs
              value={tab}
              onChange={(e, v) => setTab(v)}
              centered
            >
              {AllTabs.map(t=><Tab label={t} key={t}/>)}
            </Tabs>

            <Divider />

            <Box p={2}>

              {/* Presets */}
              {tab === 0 && (
                <List dense>
                  {PRESETS.map((pre, i) => (
                    <ListItemButton
                      key={pre.label}
                      onClick={() => applyPreset(pre, i)}
                    >
                      {preset === i ? (
                        <RadioButtonCheckedIcon color="primary" />
                      ) : (
                        <RadioButtonUncheckedIcon />
                      )}
                      <ListItemText primary={pre.label} />
                    </ListItemButton>
                  ))}
                </List>
              )}

              {/* Day */}
              {tab === 1 && (
                <DateCalendar
                  value={startDate}
                  minDate={minDate}
                  maxDate={maxDate}
                  onChange={handleDayChange}
                />
              )}

              {/* Week */}
              {tab === 2 && (
                <DateCalendar
                  value={startDate}
                  minDate={minDate}
                  maxDate={maxDate}
                  slots={{
                    day: (props) => <WeekDay {...props} selectedDay={startDate} />
                  }}
                  onChange={handleWeekChange}
                />
              )}

              {/* Month */}
              {tab === 3 && (
                <DateCalendar
                  views={['month', 'year']}
                  value={startDate}
                  minDate={minDate}
                  maxDate={maxDate}
                  onChange={handleMonthChange}
                />
              )}

              {/* Range */}
              {tab === 4 && (
                <Box display="flex" gap={2}>
                  <DateCalendar
                    value={startDate}
                    minDate={minDate}
                    maxDate={maxDate}
                    onChange={(date) => setStartDate(date)}
                  />
                  <DateCalendar
                    value={endDate}
                    minDate={minDate}
                    maxDate={maxDate}
                    onChange={(date) => setEndDate(date)}
                  />
                </Box>
              )}

              {tab !== 0 && (
                <>
                  <Divider sx={{ mt: 2 }} />

                  <Box display="flex" gap={2} mt={2}>
                    <button
                      className="btn btn-secondary w-100"
                      onClick={handleClose}
                    >
                      Cancel
                    </button>

                    <button
                      className="btn btn-primary w-100"
                      onClick={handleApply}
                    >
                      Apply
                    </button>
                  </Box>
                </>
              )}

            </Box>
          </Box>
        </LocalizationProvider>
      </Popover>
    </>
  );
};

export default DateRangeFilter;